import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from '@/components/ui/popover'
import { Button } from '@/components/ui/button'
import { m } from '@/lib/paraglide/messages'
import { cn } from '@/lib/utils'

export interface MultiSelectFilterOption {
    value: string
    label: string
}

interface Props {
    label: string
    options: MultiSelectFilterOption[]
    selectedValues: string[]
    onChange: (values: string[]) => void
    allSelected: boolean
    onAllChange: (all: boolean) => void
    /** Để trống = không lọc (xuất tất cả): không báo "chọn ít nhất 1" và không
     *  hiện tóm tắt "0". Mặc định false giữ hành vi bắt chọn cũ. */
    emptyMeansAll?: boolean
}

/**
 * MultiSelectFilter — ô lọc nhiều lựa chọn cho popup export.
 *
 * Có checkbox "Tất cả" ở đầu (tick = không lọc). Danh sách option luôn
 * hiển thị; khi tick "Tất cả" thì bỏ chọn và làm mờ (disable) các option còn
 * lại, bỏ tick mới chọn được giá trị cụ thể. Tái dùng cho kênh, người tạo lẫn
 * phương thức thanh toán.
 */
export function MultiSelectFilter({
    label,
    options,
    selectedValues,
    onChange,
    allSelected,
    onAllChange,
    emptyMeansAll = false,
}: Props) {
    const empty = !allSelected && selectedValues.length === 0
    const invalid = empty && !emptyMeansAll
    const summary = allSelected
        ? m.order_export_all_label()
        : empty && emptyMeansAll
          ? ''
          : `${selectedValues.length}`

    const toggle = (value: string, checked: boolean) => {
        if (checked) onChange([...selectedValues, value])
        else onChange(selectedValues.filter((v) => v !== value))
    }

    const toggleAll = (checked: boolean) => {
        if (checked) onChange([])
        onAllChange(checked)
    }

    return (
        <div className="grid gap-1">
            <span className="text-sm font-medium">{label}</span>
            <Popover>
                <PopoverTrigger asChild>
                    <Button
                        type="button"
                        variant="outline"
                        className="justify-between font-normal"
                    >
                        <span>{label}</span>
                        <span className="text-muted-foreground">{summary}</span>
                    </Button>
                </PopoverTrigger>
                <PopoverContent className="w-64 p-2" align="start">
                    <label className="flex items-center gap-2 py-1 text-sm">
                        <input
                            type="checkbox"
                            className="size-4 accent-primary"
                            checked={allSelected}
                            onChange={(e) => toggleAll(e.target.checked)}
                        />
                        <span>{m.order_export_all_label()}</span>
                    </label>
                    <div className="mt-1 max-h-56 overflow-auto border-t pt-1">
                        {options.map((o) => (
                            <label
                                key={o.value}
                                className={cn(
                                    'flex items-center gap-2 py-1 text-sm',
                                    allSelected &&
                                        'cursor-not-allowed opacity-50',
                                )}
                            >
                                <input
                                    type="checkbox"
                                    className="size-4 accent-primary disabled:cursor-not-allowed"
                                    disabled={allSelected}
                                    checked={
                                        !allSelected &&
                                        selectedValues.includes(o.value)
                                    }
                                    onChange={(e) =>
                                        toggle(o.value, e.target.checked)
                                    }
                                />
                                <span>{o.label}</span>
                            </label>
                        ))}
                    </div>
                </PopoverContent>
            </Popover>
            {invalid && (
                <p className="text-xs text-destructive">
                    {m.order_export_select_min_one()}
                </p>
            )}
        </div>
    )
}
